import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";

import { api } from "@/api/client";
import { ApiError } from "@/api/errors";
import { forgetRecentUser } from "@/lib/recent-users";
import type {
  AddBalanceResponse,
  AdjustmentResponse,
  AdminAdjustment,
  AmlHit,
  CardAuthVerification,
  CoinflowPaymentsResponse,
  DepositAttempt,
  IncreaseFloatResponse,
  KycSessionResponse,
  MarkUserVerifiedResponse,
  SkipPhoneVerificationResponse,
  SimulateStablesDepositInput,
  SimulateStablesDepositResponse,
  StablesRfisResponse,
  RefundResponse,
  SavedPaymentMethod,
  SavedPaymentMethodStatus,
  TransactionAlert,
  UserBankAccount,
  UserDeposit,
  UserDevice,
  UserDeviceSession,
  UserFraudWhitelist,
  UserNote,
  UserNotesResponse,
  UserPaymentLimits,
  UserPhoneVerification,
  UserRecordList,
  UserSearchResponse,
  UserSummary,
  UserTransaction,
  UserP2PTransfer,
  UserWithdrawal,
  UserWithdrawalWallet,
} from "@/api/types";

/**
 * Query keys all start with ["users", userId] so a mutation on a user can
 * invalidate the summary header and every tab in one call — the Retool page
 * re-ran user_wallet / user_transactions / user_deposits /
 * user_admin_adjustments after each action; here one prefix does it.
 */
export const usersKey = (userId: string) => ["users", userId] as const;

export const SEARCH_MIN_LENGTH = 2;

export const userSearchQuery = (q: string) => {
  const term = q.trim();
  return queryOptions({
    queryKey: ["users", "search", term],
    queryFn: ({ signal }) =>
      api.get<UserSearchResponse>(
        `/admin/users/search?q=${encodeURIComponent(term)}`,
        { signal },
      ),
    enabled: term.length >= SEARCH_MIN_LENGTH,
    staleTime: 30 * 1000,
    // Deliberately no keepPreviousData: it leaves isPending false while a new
    // term is in flight, so the results table and the header dropdown would
    // render the previous term's users — and Enter would open one of them.
    // The 300ms debounce already keeps a settled term from flickering.
  });
};

export const userSummaryQuery = (userId: string) =>
  queryOptions({
    queryKey: usersKey(userId),
    queryFn: async () => {
      const summary = await api.get<UserSummary>(
        `/admin/users/${encodeURIComponent(userId)}`,
      );
      // An older backend omits kycSessions (the dashboard can deploy first).
      return { ...summary, kycSessions: summary.kycSessions ?? [] };
    },
    staleTime: 30 * 1000,
  });

/** The per-user record lists — one route per kind, same envelope. */
export interface UserRecordKinds {
  transactions: UserTransaction;
  deposits: UserDeposit;
  "deposit-attempts": DepositAttempt;
  "bank-accounts": UserBankAccount;
  "payment-methods": SavedPaymentMethod;
  withdrawals: UserWithdrawal;
  "withdrawal-wallets": UserWithdrawalWallet;
  adjustments: AdminAdjustment;
  "transaction-alerts": TransactionAlert;
  "aml-hits": AmlHit;
  "phone-verifications": UserPhoneVerification;
  "card-auth-verifications": CardAuthVerification;
  "p2p-transfers": UserP2PTransfer;
  devices: UserDevice;
  "device-sessions": UserDeviceSession;
}
export type UserRecordKind = keyof UserRecordKinds;

export const RECORD_PAGE_SIZE = 100;

/**
 * Offset-paginated: "Load more" appends the next page instead of re-fetching
 * a bigger `limit` — the API caps `limit` at 500 (LIST_MAX_LIMIT), so a
 * growing limit hard-fails on the sixth page for a heavy user.
 */
export const userRecordsQuery = <K extends UserRecordKind>(
  userId: string,
  kind: K,
  /** Server-side search term (transactions only); "" lists everything. */
  search = "",
) =>
  infiniteQueryOptions({
    queryKey: [...usersKey(userId), kind, ...(search ? [{ search }] : [])],
    queryFn: ({ pageParam }) =>
      api.get<UserRecordList<UserRecordKinds[K]>>(
        `/admin/users/${encodeURIComponent(userId)}/${kind}?limit=${RECORD_PAGE_SIZE}&offset=${pageParam}${
          search ? `&search=${encodeURIComponent(search)}` : ""
        }`,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce(
        (count, page) => count + page.items.length,
        0,
      );
      return loaded < lastPage.total ? loaded : undefined;
    },
    staleTime: 30 * 1000,
  });

export const userNotesQuery = (userId: string) =>
  queryOptions({
    queryKey: [...usersKey(userId), "notes"],
    queryFn: () =>
      api.get<UserNotesResponse>(
        `/admin/users/${encodeURIComponent(userId)}/notes`,
      ),
    staleTime: 30 * 1000,
  });

/**
 * Coinflow merchant payments are fetched from Coinflow live (up to 20 API
 * pages), so this is click-to-load: `enabled` is false until the tab asks.
 */
export const coinflowPaymentsQuery = (
  userId: string,
  email: string,
  enabled: boolean,
) =>
  queryOptions({
    queryKey: [...usersKey(userId), "coinflow", email],
    queryFn: () =>
      api.post<CoinflowPaymentsResponse>(
        "/admin/coinflow-merchant-payments",
        { email },
        { timeoutMs: 60_000 },
      ),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : "Request failed";

/** Shared shape: toast on error, invalidate everything under the user. */
const useUserMutation = <TInput, TOutput>(
  userId: string,
  mutationFn: (input: TInput) => Promise<TOutput>,
  onSuccess?: (data: TOutput, input: TInput) => void,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess,
    onError: (error) => toast.error(errorMessage(error)),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: usersKey(userId) }),
  });
};

const usd = (value: number | string) =>
  Number(value).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

export const useAddCredits = (userId: string) =>
  useUserMutation(
    userId,
    (input: { userEmail: string; amount: number; note: string }) =>
      api.post<AddBalanceResponse>("/admin/wallet/add-balance", input),
    (data, input) =>
      toast.success(
        `Added ${usd(input.amount)} in credits — credits now ${usd(data.newCredits)}`,
      ),
  );

export const useRemoveBalance = (userId: string) =>
  useUserMutation(
    userId,
    (input: {
      userEmail: string;
      amount: number;
      note: string;
      expectedTotalBalance: number;
    }) =>
      // Retool parity: the whole balance is removed as one negative
      // adjustment against credits. `expectedTotalBalance` is the wallet
      // snapshot the amount was read from — the server re-reads the wallet
      // under its lock and rejects the adjustment if a payment or deposit
      // landed in between, rather than debiting a stale total.
      api
        .post<AdjustmentResponse>("/admin/wallet/adjustment", {
          userEmail: input.userEmail,
          amount: -Math.abs(input.amount),
          note: input.note,
          expectedTotalBalance: input.expectedTotalBalance,
        })
        .catch((error: unknown) => {
          // The backend masks BadRequest messages, and the dialog's own
          // guards (non-empty note, positive total) rule out every other 400
          // this route can return — so a 400 here is the snapshot check.
          if (error instanceof ApiError && error.status === 400) {
            throw new Error(
              "The balance changed while this was open. Reload the user and try again.",
            );
          }
          throw error;
        }),
    (data, input) =>
      toast.success(
        `Removed ${usd(input.amount)} — credits now ${usd(data.newBalance)}`,
      ),
  );

export const useRefundTransaction = (userId: string) =>
  useUserMutation(
    userId,
    (input: { transactionId: string }) =>
      api.post<RefundResponse>("/admin/wallet/refund-transaction", input),
    (data) => toast.success(`Refunded ${usd(data.refundAmount)}`),
  );

export const useIncreaseFloat = (userId: string) =>
  useUserMutation(
    userId,
    (input: { depositId: string; newFloatAmount: number }) =>
      api.post<IncreaseFloatResponse>("/admin/wallet/increase-float", input),
    (data) =>
      toast.success(
        `Float raised to ${usd(data.newFloatAmount)} (+${usd(data.floatDelta)})`,
      ),
  );

export const useUpdateUserSettings = (userId: string) =>
  useUserMutation(
    userId,
    (updates: Record<string, unknown>) =>
      api.patch<{ success: boolean; updatedFields: string[] }>(
        "/admin/user-settings",
        { userId, updates },
      ),
    (data) =>
      toast.success(
        `Saved ${data.updatedFields.length} field${data.updatedFields.length === 1 ? "" : "s"}`,
      ),
  );

export const useUpdatePaymentLimits = (userId: string) =>
  useUserMutation(
    userId,
    (paymentLimits: UserPaymentLimits | null) =>
      api.put<{ success: boolean }>("/admin/user-settings/payment-limits", {
        userId,
        paymentLimits,
      }),
    () => toast.success("Payment limits saved"),
  );

export const useBlockUser = (userId: string) =>
  useUserMutation<void, { success: boolean }>(
    userId,
    () => api.post<{ success: boolean }>("/admin/block-user", { userId }),
    () => toast.success("User blocked"),
  );

export const useReactivateUser = (userId: string) =>
  useUserMutation<void, { success: boolean }>(
    userId,
    () => api.post<{ success: boolean }>("/admin/reactivate-user", { userId }),
    () => toast.success("Account reactivated"),
  );

export const useDeactivateUser = (userId: string) =>
  useUserMutation<{ unsubscribeFromMarketing: boolean }, { success: boolean }>(
    userId,
    ({ unsubscribeFromMarketing }) =>
      api.post<{ success: boolean }>("/admin/deactivate-user", {
        userId,
        unsubscribeFromMarketing,
      }),
    () => toast.success("Account deactivated"),
  );

export type AccountDeletionBlocker =
  | "deposit"
  | "payment"
  | "withdrawal"
  | "balance"
  | "subscription"
  | "fraud"
  | "restriction"
  | "creator"
  | "payment_method"
  | "provider_registration"
  | "phone_verification";

export interface AccountDeletionEligibility {
  eligible: boolean;
  blockers: AccountDeletionBlocker[];
}

/** Whether the account can be permanently deleted, and if not, why. */
export const userDeletionEligibilityQuery = (userId: string) =>
  queryOptions({
    queryKey: [...usersKey(userId), "deletion-eligibility"],
    queryFn: () =>
      api.get<AccountDeletionEligibility>(
        `/admin/users/${encodeURIComponent(userId)}/deletion-eligibility`,
      ),
    staleTime: 30 * 1000,
  });

/**
 * Permanently deletes an account with no deposit, payment or withdrawal
 * activity (the server refuses anything else with a 409). Drops the user's
 * cached queries rather than refetching them — the user no longer exists —
 * and their Recent entry.
 */
export const useDeleteUser = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ success: boolean }>("/admin/delete-user", { userId }),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: usersKey(userId) });
      // The list, search and facet caches still hold the deleted row. Mark
      // them stale without refetching while this user's page is still mounted.
      void queryClient.invalidateQueries({
        queryKey: ["users"],
        refetchType: "none",
      });
      forgetRecentUser(userId);
      toast.success("Account deleted");
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
};

/**
 * Flips is_verified ON for a customer whose KYC session already passed. The
 * server refuses it without a `success` session, and there is no way back —
 * gate the affordance on `stats.successfulKycSessionCount` before calling.
 */
export const useMarkUserVerified = (userId: string) =>
  useUserMutation<void, MarkUserVerifiedResponse>(
    userId,
    () =>
      api.post<MarkUserVerifiedResponse>("/admin/mark-user-verified", {
        userId,
      }),
    () => toast.success("Customer marked as verified"),
  );

/**
 * Marks a phone number verified without an OTP. The server still enforces
 * that the number is unique across accounts and that a reason is given; every
 * use lands in #cust-support and as a note on the user.
 */
export const useSkipPhoneVerification = (userId: string) =>
  useUserMutation(
    userId,
    (input: { phoneNumber: string; reason: string }) =>
      api.post<SkipPhoneVerificationResponse>(
        "/admin/skip-phone-verification",
        { userId, ...input },
      ),
    (data) => toast.success(`${data.phoneNumber} marked as verified`),
  );

/**
 * Sandbox only: asks the Stables sandbox to simulate an AUD deposit on the
 * user's virtual account. Nothing is credited by this call — the deposit shows
 * up once Stables' webhooks reach the backend, hence the wording of the toast.
 */
export const useSimulateStablesDeposit = (userId: string) =>
  useUserMutation(
    userId,
    (input: SimulateStablesDepositInput) =>
      api.post<SimulateStablesDepositResponse>(
        "/admin/simulate-stables-deposit",
        { userId, ...input },
      ),
    (data) =>
      toast.success(
        `Simulated A$${data.amount} deposit (${data.scenario}) — it appears once Stables' webhook arrives`,
      ),
  );

export const useAddNote = (userId: string) =>
  useUserMutation(
    userId,
    (input: { note: string; category?: string }) =>
      api.post<{ note: UserNote }>(
        `/admin/users/${encodeURIComponent(userId)}/notes`,
        input,
      ),
    () => toast.success("Note added"),
  );

export const useVerifyBank = (userId: string) =>
  useUserMutation(
    userId,
    (input: { bankAccountId: string }) =>
      api.post<{ message: string; bankAccountId: string }>(
        "/admin/verify-bank",
        input,
      ),
    (data) => toast.success(data.message),
  );

export const useSetPaymentMethodStatus = (userId: string) =>
  useUserMutation(
    userId,
    (input: { paymentMethodId: string; status: SavedPaymentMethodStatus }) =>
      api.post<{ success: boolean; message: string }>(
        "/admin/saved-payment-method-status",
        { ...input, userId },
      ),
    (data) => toast.success(data.message),
  );

/**
 * Block or unblock a stablecoin withdrawal destination. `reason` is required by
 * the server when blocking.
 */
export const useSetWithdrawalWalletStatus = (userId: string) =>
  useUserMutation(
    userId,
    (input: {
      walletId: string;
      status: "active" | "blocked";
      reason?: string;
    }) =>
      api.post<{ success: boolean; message: string }>(
        "/admin/crypto-withdrawal-wallet-status",
        { ...input, userId },
      ),
    (data) => toast.success(data.message),
  );

/** How long the Overview's "Pause fraud checks" button pauses for. */
export const FRAUD_CHECK_PAUSE_HOURS = 24;

/**
 * Puts the user on the fraud-scoring whitelist and lifts any fraud
 * restriction already active on the account: the scorer keeps recording
 * verdicts but does not restrict the user until the entry expires. The server
 * refuses a second active entry — resume first to change the reason.
 */
export const usePauseFraudChecks = (userId: string) =>
  useUserMutation(
    userId,
    (input: { reason: string }) =>
      api.post<{ entry: UserFraudWhitelist; liftedRestrictionIds: string[] }>(
        "/admin/fraud-scoring-whitelist",
        {
          user_id: userId,
          hours: FRAUD_CHECK_PAUSE_HOURS,
          reason: input.reason,
        },
      ),
    (data) => {
      const lifted = data.liftedRestrictionIds.length;
      toast.success(
        lifted > 0
          ? `Fraud checks paused for ${FRAUD_CHECK_PAUSE_HOURS}h — ${lifted} active restriction${lifted === 1 ? "" : "s"} lifted`
          : `Fraud checks paused for ${FRAUD_CHECK_PAUSE_HOURS}h`,
      );
    },
  );

/** Ends the pause early by deleting the whitelist entry. */
export const useResumeFraudChecks = (userId: string) =>
  useUserMutation(
    userId,
    (input: { entryId: string }) =>
      api.delete<{ success: boolean }>(
        `/admin/fraud-scoring-whitelist/${encodeURIComponent(input.entryId)}`,
      ),
    () => toast.success("Fraud checks resumed"),
  );

export const useCreateKycSession = (userId: string) =>
  useUserMutation<void, KycSessionResponse>(
    userId,
    () =>
      api.post<KycSessionResponse>("/admin/create-aiprise-verification", {
        userId,
      }),
    () => toast.success("KYC session created"),
  );

/**
 * Stables compliance requests (RFIs) for one user, with their submissions
 * and uploaded files. Persisted rows only; `refreshUserStablesRfis` re-lists
 * them from Stables first (the server reports a provider failure in
 * `warnings` and still answers with the stored rows).
 */
export const userStablesRfisQuery = (userId: string) =>
  queryOptions({
    queryKey: [...usersKey(userId), "stables-rfis"] as const,
    queryFn: ({ signal }) =>
      api.get<StablesRfisResponse>(
        `/admin/users/${encodeURIComponent(userId)}/stables-rfis`,
        { signal },
      ),
    staleTime: 30 * 1000,
  });

export const useRefreshUserStablesRfis = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.get<StablesRfisResponse>(
        `/admin/users/${encodeURIComponent(userId)}/stables-rfis?refresh=true`,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(userStablesRfisQuery(userId).queryKey, data);
      if (data.warnings.length > 0) {
        toast.warning(data.warnings.join(" "));
      } else {
        toast.success("Compliance requests refreshed from Stables");
      }
    },
    onError: (error) => {
      toast.error(
        error instanceof ApiError ? error.message : "Could not refresh",
      );
    },
  });
};
